import * as React from "react";
import { useRef } from "react";
import AsyncAction from "../AsyncAction";

interface SettingLogoProps {
  isDefault: boolean;
  logoUrl?: string;
  maxSizeInBytes: number;
  supportedTypes: string;
  displayError: (message: string) => any;
  onLogoChange: (blob: Blob) => void;
  onLogoReset: () => void;
}

const SettingLogo = (props: SettingLogoProps) => {
  const refFileInput = useRef<any>(null);
  const refAsyncAction = useRef<AsyncAction>(null);

  const uploadLogo = async () => {
    const file: File = refFileInput.current.files[0];
    resizeImageWithResolution(file, 720, 120).then((blob: Blob) => {
      props.onLogoChange(blob);
    });
  };

  const onFileInputChange = () => {
    const supportedTypes = props.supportedTypes.split(",");
    if (refFileInput.current.files.length > 0) {
      let file: File = refFileInput.current.files[0];
      if (file.size > props.maxSizeInBytes) {
        props.displayError(i18n["Page.Settings.Customisation.Logo.Errors.Exceeding"]);
      } else if (file.type! in supportedTypes) {
        props.displayError(i18n["Page.Settings.Customisation.Logo.Errors.Unsupported"]);
      } else {
        refAsyncAction.current.performAsyncAction();
      }
    }
  };

  const resizeImageWithResolution = (file: File, maxWidth: number, maxHeight: number) => {
    return new Promise((resolve, reject) => {
      let image = new Image();
      image.src = URL.createObjectURL(file);
      image.onload = () => {
        let width = image.width;
        let height = image.height;

        if (width <= maxWidth && height <= maxHeight) {
          resolve(file);
        }

        let newWidth: number;
        let newHeight: number;

        if (width > height) {
          newHeight = height * (maxWidth / width);
          newWidth = maxWidth;
        } else {
          newWidth = width * (maxHeight / height);
          newHeight = maxHeight;
        }

        let canvas = document.createElement("canvas");
        canvas.width = newWidth;
        canvas.height = newHeight;
        let context = canvas.getContext("2d");
        context.drawImage(image, 0, 0, newWidth, newHeight);
        canvas.toBlob(resolve, file.type);
      };
      image.onerror = reject;
    });
  };

  const restoreDefault = async () => {
    props.onLogoReset();
  };

  return (
    <div className="setting-grid">
      <div className="item-highlight">{i18n["Page.Settings.Customisation.Logo.Select"]}</div>
      <div className="h-flex with-gap">
        {props.logoUrl && !props.isDefault && (
          <div className="scan-image-input fit-image no-border">
            <div className="image-container">
              <div className={"h-flex centered"}>
                <img id={"logo-preview"} src={props.logoUrl} alt="Branding Logo preview" />
              </div>
            </div>
          </div>
        )}
        <div style={{ alignSelf: "center" }}>
          <input
            id={"logo-file-input"}
            accept={props.supportedTypes}
            type="file"
            ref={refFileInput}
            className="none"
            onChange={onFileInputChange}
          />
          <div>
            <div className="h-flex with-margins align-center">
              <AsyncAction
                buttonId={"upload-logo-btn"}
                buttonLabel={i18n["Global.Upload"]}
                ref={refAsyncAction}
                onClick={(e) => {
                  e.preventDefault();
                  refFileInput.current.click();
                }}
                promise={uploadLogo}
              />
              {props.logoUrl && !props.isDefault && (
                <AsyncAction
                  buttonId={"restore-default-logo-btn"}
                  buttonLabel={i18n["Page.Settings.Customisation.Restore.Default"]}
                  promise={() => restoreDefault()}
                  buttonType={"button-secondary small"}
                />
              )}
            </div>
            <ul className="setting-logo-details">
              <li>{i18n["Page.Settings.Customisation.Logo.Details.Limit.Size"]}</li>
              <li>{i18n["Page.Settings.Customisation.Logo.Details.Supported"]}</li>
              <li>{i18n["Page.Settings.Customisation.Logo.Details.Limit.Resolution"]}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingLogo;
